import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { captureSubmissionError } from "../../utils/sentryUtils";
import SubmittingCard from "../../components/SubmittingCard";
import SubmissionErrorCard from "../../components/SubmissionErrorCard";
import Card from "../../components/Card";
import Action from "../../components/Action";
import Confirmation from "../../components/Confirmation";
import Image from "../../components/Image";
import QuestionDescription from "../../components/QuestionDescription";
import ScrollToTop from "../../components/utils/ScrollToTop";
import StepFlowNavigate from "../../components/StepFlowNavigate";
import FileUploadQuestion from "../../components/FileUploadQuestion";
import RadioQuestion from "../../components/RadioQuestion";
import imageOfficeWorking from "../../images/office-working.png";
import { sanitiseHtml } from "../../utils/sanitiseHtml";

/**
 * Groups document identifiers into logical choices for the selection step.
 * Returns an array of { value, label, documentIdentifiers } objects.
 *
 * For identity document requests, groups by document type (passport, driving licence, national ID card).
 * The selfie is treated as always-required and not included in any group.
 *
 * For requests with a single non-selfie document (e.g. proof of address), returns an empty array
 * so the selection step is skipped.
 */
export const buildDocumentGroups = (documents) => {
  const nonSelfieDocuments = documents.filter((d) => !d.identifier.includes("selfie"));

  // If there's only one non-selfie document, no selection needed
  if (nonSelfieDocuments.length <= 1) {
    return [];
  }

  const groups = [];

  const passportDocs = nonSelfieDocuments.filter((d) => d.identifier.includes("passport"));
  if (passportDocs.length > 0) {
    groups.push({
      value: "passport",
      label: "Passport",
      documentIdentifiers: passportDocs.map((d) => d.identifier),
    });
  }

  const drivingLicenceDocs = nonSelfieDocuments.filter((d) => d.identifier.includes("driving_licence"));
  if (drivingLicenceDocs.length > 0) {
    groups.push({
      value: "driving_licence",
      label: "Driving licence",
      documentIdentifiers: drivingLicenceDocs.map((d) => d.identifier),
    });
  }

  const nationalIdDocs = nonSelfieDocuments.filter((d) => d.identifier.includes("national_identity_card"));
  if (nationalIdDocs.length > 0) {
    groups.push({
      value: "national_identity_card",
      label: "National identity card",
      documentIdentifiers: nationalIdDocs.map((d) => d.identifier),
    });
  }

  // If no known groups were matched, treat all documents as ungrouped
  // (selection step will be skipped)
  if (groups.length === 0) {
    return [];
  }

  return groups;
};

/**
 * Returns the documents that should be shown for upload,
 * based on the user's document type selection.
 */
export const getVisibleDocuments = (documents, documentGroups, documentChoice) => {
  const selfieDocuments = documents.filter((d) => d.identifier.includes("selfie"));

  if (documentGroups.length === 0 || !documentChoice) {
    // No grouping — show all documents
    return documents;
  }

  const selectedGroup = documentGroups.find((g) => g.value === documentChoice);
  if (!selectedGroup) {
    return documents;
  }

  const selectedDocuments = documents.filter((d) => selectedGroup.documentIdentifiers.includes(d.identifier));
  return [...selectedDocuments, ...selfieDocuments];
};

// --- Step components ---

const DocumentTypeSelection = ({ documentGroups, documentChoice, onStepComplete }) => {
  // Seeded from the flow state, which already holds the answer. Without it a client who
  // comes back to this step sees nothing selected and is asked to choose again.
  const [selected, setSelected] = useState(documentChoice || null);
  const [validationError, setValidationError] = useState(null);

  const handleContinue = () => {
    if (!selected) {
      setValidationError("Please choose an option");
      return;
    }
    onStepComplete({ documentChoice: selected });
  };

  return (
    <>
      <ScrollToTop />
      <Card title="Which identity document would you like to upload?" removeTopPadding>
        <QuestionDescription>
          <p>Please choose the document you’d like to use. You’ll upload photos of it on the next screen.</p>
        </QuestionDescription>
        <RadioQuestion
          question="Document type"
          hideLegend
          name="documentChoice"
          options={documentGroups}
          selected={selected}
          optionSelected={(value) => {
            setSelected(value);
            setValidationError(null);
          }}
          validationError={validationError}
        />
        <Action small onClick={handleContinue}>
          Continue
        </Action>
      </Card>
    </>
  );
};

// Flattens the per-document uploads into the payload the submission endpoint wants.
// Only the documents on screen count: the client may have attached files to a document
// type and then chosen a different one, and those uploads are no longer part of the request.
const toUploadedDocuments = (uploadsByDocument = {}, visibleDocuments = []) =>
  visibleDocuments.flatMap((doc) =>
    (uploadsByDocument[doc.identifier] || []).map((upload) => ({
      identifier: doc.identifier,
      upload_id: upload.upload_id,
    })),
  );

const DocumentUploadStep = ({
  doable,
  visibleDocuments,
  uploadUrl,
  uploadsByDocument,
  onUploadsChanged,
  onStepComplete,
}) => {
  const [uploadsInProgress, setUploadsInProgress] = useState({});
  const [errors, setErrors] = useState({});

  const handleUploadsChanged = (identifier) => (documentUploads) => {
    onUploadsChanged(identifier, documentUploads);
    setErrors((prev) => ({ ...prev, [identifier]: false }));
  };

  const handleUploadInProgress = (identifier) => (inProgress) => {
    setUploadsInProgress((prev) => ({ ...prev, [identifier]: inProgress }));
  };

  const handleSubmit = () => {
    // Every visible document is required. We need one complete identity document plus a
    // selfie, because a partial set leaves the staff review task blocked. Only the documents
    // on screen count, so a document the client moved away from never satisfies this.
    const missingDocuments = visibleDocuments.filter((doc) => (uploadsByDocument[doc.identifier] || []).length === 0);
    if (missingDocuments.length > 0) {
      setErrors(Object.fromEntries(missingDocuments.map((doc) => [doc.identifier, true])));
      return;
    }

    onStepComplete({});
  };

  const buttonDisabled = Object.values(uploadsInProgress).some((v) => v);
  const multipleDocuments = visibleDocuments.length > 1;
  const documentsText = multipleDocuments ? "documents" : "document";

  return (
    <>
      <ScrollToTop />
      <Card title={doable.title} removeTopPadding>
        {doable.description && (
          <QuestionDescription dangerouslySetInnerHTML={{ __html: sanitiseHtml(doable.description) }} />
        )}
        {visibleDocuments.map((doc) => (
          <FileUploadQuestion
            key={doc.identifier}
            question={doc.display_name}
            multiple={!!doc.allow_multiple_files}
            doableId={doable.id}
            uploadUrl={uploadUrl}
            defaultUploads={uploadsByDocument[doc.identifier]}
            onUploadsChanged={handleUploadsChanged(doc.identifier)}
            onUploadInProgress={handleUploadInProgress(doc.identifier)}
            validationError={errors[doc.identifier] ? "Please upload a file" : null}
          />
        ))}
        <Action small disabled={buttonDisabled} onClick={handleSubmit}>
          Submit your {documentsText}
        </Action>
      </Card>
    </>
  );
};

const DocumentSubmissionStep = ({ doable, uploadedDocuments, onFlowComplete }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasInitiated = useRef(false);

  useEffect(() => {
    if (hasInitiated.current) return;
    hasInitiated.current = true;

    if (!uploadedDocuments || uploadedDocuments.length === 0) {
      setError("No documents provided");
      setLoading(false);
      return;
    }

    fetch(`/api/anonymous_doable/${doable.id}`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ uploaded_documents: uploadedDocuments }),
    }).then(
      async (resp) => {
        if (resp.ok) {
          setLoading(false);
          onFlowComplete();
        } else if (resp.status === 409) {
          setError(<p>You’ve already uploaded documents for this request.</p>);
          setLoading(false);
        } else {
          const body = await resp.text().catch(() => "");
          captureSubmissionError("AnonymousDocumentUpload", resp.status, body);
          setError("Unable to submit your documents");
          setLoading(false);
        }
      },
      (err) => {
        console.error("Anonymous document upload submission failed:", err);
        setError("Unable to submit your documents");
        setLoading(false);
      },
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- fires once on mount; ref guard prevents StrictMode re-fire

  if (loading) {
    return <SubmittingCard />;
  }

  if (error) {
    return <SubmissionErrorCard error={error} />;
  }

  return null;
};

/**
 * Anonymous document upload flow using StepFlowNavigate.
 *
 * Steps:
 * 1. Document type selection (skipped if only one document type or no grouping applies)
 * 2. File upload with validation
 * 3. Auto-submission (fires API call on mount, shows loading then success/error)
 *
 * On successful submission, the flow completes and shows a confirmation message.
 */
const AnonymousDocumentUploadFlow = ({ doable, pageWidth }) => {
  const documents = doable.configuration.documents || [];
  const documentGroups = buildDocumentGroups(documents);
  const hasSelection = documentGroups.length > 0;
  const uploadUrl = `/api/anonymous_doable/${doable.id}/upload_file`;

  const [flowCompleted, setFlowCompleted] = useState(false);
  // Held here, not in the step or the flow state, because this component outlives every
  // step. The step unmounts whenever the client moves, and the flow state is only written
  // when a step completes, so neither survives a client going back to change an answer.
  const [uploadsByDocument, setUploadsByDocument] = useState({});

  const handleUploadsChanged = (identifier, uploads) =>
    setUploadsByDocument((prev) => ({ ...prev, [identifier]: uploads }));

  if (flowCompleted) {
    return (
      <>
        <ScrollToTop />
        <Confirmation title="Thanks! We’ve got your documents" pageWidth={pageWidth}>
          <p>We’ll review your documents and get back to you if we need anything else.</p>
        </Confirmation>
        <Image src={imageOfficeWorking} fullWidth={true} />
      </>
    );
  }

  return (
    <StepFlowNavigate
      initialFlowState={{}}
      steps={[
        {
          name: "Select document type",
          slug: "select-document",
          valid: () => hasSelection,
          component: (flowState) => (
            <DocumentTypeSelection documentGroups={documentGroups} documentChoice={flowState.documentChoice} />
          ),
        },
        {
          name: "Upload documents",
          slug: "upload",
          component: (flowState) => {
            const visibleDocuments = getVisibleDocuments(documents, documentGroups, flowState.documentChoice);
            return (
              <DocumentUploadStep
                doable={doable}
                visibleDocuments={visibleDocuments}
                uploadUrl={uploadUrl}
                uploadsByDocument={uploadsByDocument}
                onUploadsChanged={handleUploadsChanged}
              />
            );
          },
        },
        {
          name: "Submission",
          slug: "submit",
          component: (flowState) => (
            <DocumentSubmissionStep
              doable={doable}
              uploadedDocuments={toUploadedDocuments(
                uploadsByDocument,
                getVisibleDocuments(documents, documentGroups, flowState.documentChoice),
              )}
              onFlowComplete={() => setFlowCompleted(true)}
            />
          ),
        },
      ]}
    />
  );
};

AnonymousDocumentUploadFlow.propTypes = {
  doable: PropTypes.shape({
    id: PropTypes.string.isRequired,
    doable_type: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string,
    configuration: PropTypes.shape({
      documents: PropTypes.arrayOf(
        PropTypes.shape({
          identifier: PropTypes.string.isRequired,
          display_name: PropTypes.string.isRequired,
          allow_multiple_files: PropTypes.bool,
        }),
      ),
    }).isRequired,
  }).isRequired,
  pageWidth: PropTypes.oneOf(["narrow", "regular", "wide", "wider"]),
};

export default AnonymousDocumentUploadFlow;
